import axios, { AxiosResponse, AxiosError } from 'axios'

// Environment variables with fallbacks for development
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:9000'
const API_VERSION = import.meta.env.VITE_API_VERSION || 'v1'

export interface BackMarketSyncInfo {
  status: string
  importId?: string
  error?: string
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

class ApiService {
  private api = axios.create({
    baseURL: `${API_BASE_URL}/api/${API_VERSION}`,
    headers: {
      'Content-Type': 'application/json',
    },
  })
  private isRefreshing = false
  private refreshPromise: Promise<any> | null = null

  constructor() {
    // Request interceptor to add auth token
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('admin_token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    })

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Don't handle 401 errors for login, verify-2fa, or refresh requests
          const isLoginRequest = error.config?.url?.includes('/admin/auth/login')
          const isVerify2FARequest = error.config?.url?.includes('/admin/auth/verify-2fa')
          const isRefreshRequest = error.config?.url?.includes('/admin/auth/refresh')
          
          if (isLoginRequest || isVerify2FARequest) {
            const message = (error.response?.data as any)?.detail || error.message || 'An error occurred'
            throw new ApiError(error.response?.status || 0, message)
          }
          
          if (isRefreshRequest) {
            // Refresh token is invalid, clear everything and redirect
            this.clearTokensAndRedirect()
            throw new ApiError(error.response?.status || 0, 'Session expired')
          }
          
          // For other requests, try to refresh token first
          const refreshToken = localStorage.getItem('admin_refresh_token')
          if (refreshToken && error.config && !this.isRefreshing) {
            try {
              // Prevent multiple concurrent refresh attempts
              if (this.refreshPromise) {
                await this.refreshPromise
              } else {
                this.refreshPromise = this.refreshToken()
                await this.refreshPromise
                this.refreshPromise = null
              }
              
              // Retry the original request with new token
              const token = localStorage.getItem('admin_token')
              if (token && error.config) {
                error.config.headers.Authorization = `Bearer ${token}`
                return this.api.request(error.config)
              }
            } catch (refreshError) {
              // Refresh failed, clear tokens and redirect to login
              this.clearTokensAndRedirect()
              throw new ApiError(401, 'Session expired')
            }
          } else {
            // No refresh token or already refreshing, clear everything and redirect to login
            this.clearTokensAndRedirect()
            throw new ApiError(401, 'Authentication required')
          }
        }
        
        const message = (error.response?.data as any)?.detail || error.message || 'An error occurred'
        throw new ApiError(error.response?.status || 0, message)
      }
    )
  }

  private getHeaderValue(value?: string | string[]) {
    if (!value) return undefined
    return Array.isArray(value) ? value[0] : value
  }

  private parseBackMarketSyncHeaders(headers?: AxiosResponse['headers']): BackMarketSyncInfo | null {
    if (!headers) return null
    const headerBag = headers as unknown as Record<string, string | string[] | undefined>
    const status = this.getHeaderValue(headerBag['x-backmarket-sync-status'])
    if (!status) {
      return null
    }

    const importId = this.getHeaderValue(headerBag['x-backmarket-import-id'])
    const error = this.getHeaderValue(headerBag['x-backmarket-sync-error'])

    return {
      status,
      ...(importId ? { importId } : {}),
      ...(error ? { error } : {}),
    }
  }

  private clearTokensAndRedirect() {
    localStorage.removeItem('admin_token')
    localStorage.removeItem('admin_refresh_token')
    localStorage.removeItem('admin_user')
    // Only redirect if we're not already on the login page
    if (window.location.pathname !== '/admin/login') {
      window.location.href = '/admin/login'
    }
  }

  // Admin Auth endpoints
  async login(email: string, password: string) {
    try {
      const response = await this.api.post<{
        access_token?: string
        token_type?: string
        refresh_token?: string
        admin?: {
          id: number
          email: string
          full_name: string
          is_active: boolean
          is_superuser: boolean
          role: string
          permissions_list: string[]
        }
        requires_2fa?: boolean
        temp_token?: string
        message?: string
      }>('/admin/auth/login', { email, password })
      return response.data
    } catch (error) {
      // Handle login-specific errors
      if (error instanceof ApiError) {
        if (error.status === 401) {
          throw new ApiError(401, 'Invalid email or password')
        } else if (error.status === 403) {
          throw new ApiError(403, 'Account is inactive or lacks permissions')
        }
        throw error
      }
      // Handle other errors
      const message = (error as any)?.response?.data?.detail || (error as any)?.message || 'Login failed'
      throw new ApiError((error as any)?.response?.status || 500, message)
    }
  }

  async verify2faAdmin(tempToken: string, token: string) {
    const response = await this.api.post<{
      access_token: string
      token_type: string
      refresh_token: string
      admin: {
        id: number
        email: string
        full_name: string
        is_active: boolean
        is_superuser: boolean
        role: string
        permissions_list: string[]
      }
    }>('/admin/auth/verify-2fa', { temp_token: tempToken, token })
    return response.data
  }

  async refreshToken() {
    if (this.isRefreshing) {
      throw new ApiError(401, 'Refresh already in progress')
    }
    
    const refreshToken = localStorage.getItem('admin_refresh_token')
    if (!refreshToken) {
      throw new ApiError(401, 'No refresh token available')
    }
    
    this.isRefreshing = true
    
    try {
      const response = await this.api.post<{
        access_token: string
        token_type: string
        refresh_token: string
      }>('/admin/auth/refresh', { refresh_token: refreshToken })
      
      // Update stored tokens
      localStorage.setItem('admin_token', response.data.access_token)
      localStorage.setItem('admin_refresh_token', response.data.refresh_token)
      
      return response.data
    } catch (error) {
      // If refresh fails, clear tokens
      this.clearTokensAndRedirect()
      const message = (error as any)?.response?.data?.detail || (error as any)?.message || 'Token refresh failed'
      throw new ApiError((error as any)?.response?.status || 401, message)
    } finally {
      this.isRefreshing = false
    }
  }

  async getCurrentAdmin() {
    const response = await this.api.get<{
      id: number
      email: string
      full_name: string
      is_active: boolean
      is_superuser: boolean
      role: string
      permissions_list: string[]
    }>('/admin/auth/me')
    return response.data
  }

  // Admin dashboard stats
  async getDashboardStats() {
    const response = await this.api.get<{
      total_users: number
      total_products: number
      total_orders: number
      total_revenue: number
      new_users_30d: number
      new_orders_30d: number
      revenue_30d: number
    }>('/admin/stats/overview')
    return response.data
  }

  // Users management
  async getUsers(params?: { skip?: number; limit?: number; search?: string }) {
    const response = await this.api.get<Array<{
      id: number
      email: string
      full_name: string
      is_active: boolean
      is_superuser: boolean
      is_verified: boolean
      created_at: string
    }>>('/admin/users', { params })
    return response.data
  }

  async getUser(userId: number) {
    const response = await this.api.get<{
      id: number
      email: string
      full_name: string
      is_active: boolean
      is_superuser: boolean
      is_verified: boolean
      created_at: string
    }>(`/admin/users/${userId}`)
    return response.data
  }

  async updateUserStatus(userId: number, is_active: boolean) {
    const response = await this.api.put<{ message: string; user: any }>(
      `/admin/users/${userId}/status`,
      { is_active }
    )
    return response.data
  }

  async deleteUser(userId: number, anonymize_orders: boolean = true) {
    const response = await this.api.delete<{ message: string; deletion_summary: any }>(
      `/admin/users/${userId}`,
      { params: { anonymize_orders } }
    )
    return response.data
  }

  // Admin users management
  async getAdminUsers(params?: { skip?: number; limit?: number; search?: string }) {
    const response = await this.api.get<Array<{
      id: number
      email: string
      full_name: string
      is_active: boolean
      is_superuser: boolean
      role: string
      permissions_list: string[]
      created_at: string
    }>>('/admin/admins', { params })
    return response.data
  }

  async createAdminUser(data: { 
    email: string
    full_name: string
    password: string
    role_ids: number[]
  }) {
    // Convert to backend expected format
    const backendData = {
      email: data.email,
      full_name: data.full_name,
      password: data.password,
      role_ids: data.role_ids,
      role: 'admin' // Default role for backward compatibility
    }
    
    const response = await this.api.post<{
      id: number
      email: string
      full_name: string
      is_active: boolean
      is_superuser: boolean
      role: string
      roles: Array<{
        id: number
        name: string
        display_name: string
      }>
      permissions: string[]
      created_at: string
    }>('/admin/admins', backendData)
    return response.data
  }

  async updateAdminUser(adminId: number, data: { 
    email?: string
    full_name?: string
    role?: string
    permissions_list?: string[]
    is_active?: boolean
  }) {
    // Convert to backend expected format
    const backendData: any = {
      email: data.email,
      full_name: data.full_name,
      role: data.role,
      is_active: data.is_active
    }
    
    // Only include permissions if provided
    if (data.permissions_list) {
      backendData.permissions = data.permissions_list
    }
    
    const response = await this.api.put<{
      id: number
      email: string
      full_name: string
      is_active: boolean
      is_superuser: boolean
      role: string
      permissions_list: string[]
      updated_at: string
    }>(`/admin/admins/${adminId}`, backendData)
    return response.data
  }

  async deleteAdminUser(adminId: number) {
    const response = await this.api.delete<{ message: string }>(
      `/admin/admins/${adminId}`
    )
    return response.data
  }

  // User activities and cart management
  async getUserActivities(userId: number, params?: { skip?: number; limit?: number }) {
    const response = await this.api.get<Array<{
      id: number
      type: 'login' | 'order' | 'cart_update' | 'profile_update'
      description: string
      created_at: string
      metadata: any
    }>>(`/admin/users/${userId}/activities`, { params })
    return response.data
  }

  async getUserCart(userId: number) {
    const response = await this.api.get<{
      id: number
      user_id: number
      items: Array<{
        id: number
        product_id: number
        product_name: string
        quantity: number
        unit_price: number
        total_price: number
        added_at: string
      }>
      total_items: number
      total_amount: number
      updated_at: string
    }>(`/admin/users/${userId}/cart`)
    return response.data
  }

  async getUserOrders(userId: number, params?: { skip?: number; limit?: number }) {
    const response = await this.api.get<Array<{
      id: number
      order_number: string
      total_amount: number
      status: string
      payment_status: string
      created_at: string
      order_items: Array<{
        id: number
        product_id: number
        product_name: string
        quantity: number
        unit_price: number
        total_price: number
      }>
    }>>(`/admin/users/${userId}/orders`, { params })
    return response.data
  }

  // Roles and permissions management
  async getRoles() {
    const response = await this.api.get<Array<{
      id: number
      name: string
      display_name: string
      description: string
      is_active: boolean
      is_system_role: boolean
      permissions: Array<{
        id: number
        name: string
        display_name: string
        description: string
        resource: string
        action: string
      }>
      created_at: string
      updated_at: string
    }>>('/admin/roles')
    return response.data
  }

  async getPermissions() {
    const response = await this.api.get<Array<{
      id: number
      name: string
      display_name: string
      description: string
      resource: string
      action: string
      is_active: boolean
      created_at: string
      updated_at: string
    }>>('/admin/permissions')
    return response.data
  }

  async createRole(data: {
    name: string
    display_name: string
    description: string
    is_active?: boolean
    permissions: string[]
  }) {
    const response = await this.api.post<{
      id: number
      name: string
      display_name: string
      description: string
      is_active: boolean
      is_system_role: boolean
      permissions: Array<{
        id: number
        name: string
        display_name: string
        description: string
        resource: string
        action: string
      }>
      created_at: string
      updated_at: string
    }>('/admin/roles', data)
    return response.data
  }

  async updateRole(roleId: number, data: {
    name?: string
    display_name?: string
    description?: string
    is_active?: boolean
    permissions?: string[]
  }) {
    const response = await this.api.put<{
      id: number
      name: string
      display_name: string
      description: string
      is_active: boolean
      is_system_role: boolean
      permissions: Array<{
        id: number
        name: string
        display_name: string
        description: string
        resource: string
        action: string
      }>
      created_at: string
      updated_at: string
    }>(`/admin/roles/${roleId}`, data)
    return response.data
  }

  async deleteRole(roleId: number) {
    const response = await this.api.delete<{ message: string }>(`/admin/roles/${roleId}`)
    return response.data
  }

  // Orders management
  async getOrders(params?: { skip?: number; limit?: number; status?: string; search?: string }) {
    const response = await this.api.get<Array<{
      id: number
      order_number: string
      user_id: number
      total_amount: number
      status: string
      payment_status: string
      created_at: string
    }>>('/admin/orders', { params })
    return response.data
  }

  async getOrder(orderId: number) {
    const response = await this.api.get<{
      id: number
      order_number: string
      user_id: number
      total_amount: number
      subtotal?: number
      discount_amount?: number
      coupon_code?: string
      tax_amount?: number
      shipping_amount?: number
      status: string
      payment_status: string
      payment_method?: string
      shipping_address?: string
      billing_address?: string
      notes?: string
      cancellation_reason?: string
      cancelled_at?: string
      created_at: string
      updated_at?: string
      order_items: Array<{
        id: number
        product_id: number
        quantity: number
        unit_price: number
        total_price: number
        product?: {
          id: number
          name: string
          sku: string
          image_url?: string
          slug?: string
        }
      }>
      shipping_labels?: Array<{
        id: number
        carrier: string
        service_level: string
        tracking_number?: string
        label_url?: string
        status?: string
        tracking_status?: string
        master_tracking_number?: string
        parcel_tracking_numbers?: string[]
        is_multi_piece?: string
      }>
    }>(`/admin/orders/${orderId}`)
    return response.data
  }

  async updateOrderStatus(orderId: number, status: string, cancellationReason?: string) {
    const body: any = { status }
    if (cancellationReason) body.cancellation_reason = cancellationReason
    const response = await this.api.put<{ message: string; order: any }>(
      `/admin/orders/${orderId}/status`,
      body
    )
    return response.data
  }

  async updateOrderTracking(
    orderId: number,
    data: { tracking_number: string; carrier: string; estimated_delivery?: string }
  ) {
    const response = await this.api.put<{ message: string; order: any }>(
      `/admin/orders/${orderId}/tracking`,
      data
    )
    return response.data
  }

  async getTracking(trackingNumber: string, carrier?: string) {
    const response = await this.api.get<{
      tracking_number: string
      carrier: string
      status: string
      status_details?: string
      status_date?: string
      eta?: string
      tracking_status?: string
      tracking_history?: Array<{
        status: string
        status_details: string
        status_date: string
        location?: {
          city?: string
          state?: string
          zip?: string
          country?: string
        }
      }>
    }>(`/shipping/track/${trackingNumber}`, {
      params: carrier ? { carrier } : undefined
    })
    return response.data
  }

  async downloadInvoice(orderId: number, format: 'pdf' | 'html' = 'pdf') {
    const response = await this.api.get(`/admin/orders/${orderId}/invoice`, {
      params: { format },
      responseType: 'blob'
    })
    return response.data
  }

  // Recent activity
  async getRecentActivity(limit: number = 10) {
    const response = await this.api.get<Array<{
      type: 'order' | 'user'
      id: number
      title: string
      description: string
      timestamp: string
      status: string
    }>>('/admin/recent-activity', { params: { limit } })
    return response.data
  }

  // Categories management
  async getCategories(params?: { skip?: number; limit?: number; is_active?: boolean; search?: string }) {
    const response = await this.api.get<{
      items: Array<{
        id: number
        name: string
        slug: string
        description: string
        is_active: boolean
        meta_title?: string
        meta_description?: string
        created_at: string
        updated_at: string
      }>
      total: number
      page: number
      per_page: number
      pages: number
    }>('/categories', { params })
    return response.data
  }

  async getCategory(categoryId: number) {
    const response = await this.api.get<{
      id: number
      name: string
      slug: string
      description: string
      is_active: boolean
      meta_title?: string
      meta_description?: string
      created_at: string
      updated_at: string
    }>(`/categories/${categoryId}`)
    return response.data
  }

  async createCategory(data: { name: string; description?: string; is_active?: boolean }) {
    const response = await this.api.post<{
      id: number
      name: string
      slug: string
      description: string
      is_active: boolean
      created_at: string
      updated_at: string
    }>('/categories', data)
    return response.data
  }

  async updateCategory(categoryId: number, data: { name?: string; description?: string; is_active?: boolean }) {
    const response = await this.api.put<{
      id: number
      name: string
      slug: string
      description: string
      is_active: boolean
      created_at: string
      updated_at: string
    }>(`/categories/${categoryId}`, data)
    return response.data
  }

  async updateCategorySeo(categoryId: number, data: {
    meta_title?: string
    meta_description?: string
  }) {
    const response = await this.api.put<{
      id: number
      name: string
      slug: string
      description: string
      is_active: boolean
      meta_title?: string
      meta_description?: string
      created_at: string
      updated_at: string
    }>(`/categories/${categoryId}`, data)
    return response.data
  }

  async deleteCategory(categoryId: number) {
    await this.api.delete(`/categories/${categoryId}`)
  }

  // Products management
  async getProducts(params?: { 
    skip?: number
    limit?: number
    search?: string
    category?: string
    status?: string
    source?: string
  }) {
    const response = await this.api.get<{
      items: Array<{
        id: number
        name: string
        sku: string
        description: string
        short_description?: string
        meta_title?: string
        meta_description?: string
        price: number | string
        combined_sale_percentage?: number
        category: string | {
          id: number
          name: string
          slug: string
          description?: string
          image_url?: string
          icon?: string
          is_active?: boolean
          sort_order?: number
          meta_title?: string
          meta_description?: string
          created_at?: string
          updated_at?: string
        }
        brand?: string
        stock_quantity: number
        is_active: boolean
        is_featured: boolean
        is_on_sale: boolean
        sale_percentage: number
        sync_enabled?: boolean
        condition?: string
        main_image?: string
        images?: string[]
        created_at: string
        updated_at: string
        external_id?: string
        external_metadata?: {
          backmarket_data?: any
          synced_at?: string
          warranty_delay?: number
          backmarket_id?: string
          publication_state?: number
          state?: number
          snowden?: boolean
          grade?: string
          listing_id?: string
          product_id?: string
        }
      }>
      total: number
      page: number
      per_page: number
      pages: number
      has_next: boolean
      has_prev: boolean
    }>('/admin/products', { params })
    return response.data
  }

  async getProduct(productId: number) {
    const response = await this.api.get<{
      id: number
      name: string
      sku: string
      description: string
      short_description?: string
      meta_title?: string
      meta_description?: string
      price: number
      original_price?: number
      category: string | {
        id: number
        name: string
        slug: string
        description?: string
        image_url?: string
        icon?: string
        is_active?: boolean
        sort_order?: number
        meta_title?: string
        meta_description?: string
        created_at?: string
        updated_at?: string
      }
      brand?: string
      stock_quantity: number
      is_active: boolean
      is_featured: boolean
      is_on_sale: boolean
      sale_percentage: number
      sync_enabled?: boolean
      condition?: string
      weight?: number
      dimensions?: string
      tags?: string[]
      main_image?: string
      images?: string[]
      created_at: string
      updated_at: string
    }>(`/admin/products/${productId}`)
    return response.data
  }

  async getProductPricing(productId: number) {
    const response = await this.api.get<{
      product_id: number
      price: number | null
      manual_sale_percentage: number
      effective_sale_percentage: number
      effective_source: { type: string; label: string; percentage: number; code?: string } | null
      combined_sale_percentage: number
      combined_breakdown: Array<{ label: string; percentage: number; code?: string }>
      discount_sources: Array<{ type: string; label: string; percentage: number; code?: string }>
      applicable_coupons: Array<{
        code: string
        description?: string | null
        discount_type: string
        discount_value: number
        percentage: number | null
        applies_to: string
        auto_apply: boolean
        exclusive: boolean
      }>
    }>(`/admin/products/${productId}/pricing`)
    return response.data
  }

  async createProduct(data: {
    name: string
    description?: string
    short_description?: string
    price: number
    original_price?: number
    category_id?: number
    brand?: string
    sku?: string
    stock_quantity: number
    is_active?: boolean
    is_featured?: boolean
    is_on_sale?: boolean
    sale_percentage?: number
    sync_enabled?: boolean
    condition?: string
    weight?: number
    dimensions?: string
    ean?: string
    mpn?: string
    width?: number
    height?: number
    depth?: number
    tags?: string[]
    specifications?: Record<string, string>
    main_image?: string
    images?: string[]
  }, options?: { syncToBackMarket?: boolean; publishToBackMarket?: boolean }): Promise<{
    product: {
      id: number
      name: string
      sku: string
      description: string
      price: number
      category: string
      brand?: string
      stock_quantity: number
      is_active: boolean
      created_at: string
    }
    backMarketSync: BackMarketSyncInfo | null
  }> {
    const params: Record<string, any> = {}
    if (options?.syncToBackMarket) {
      params.sync_to_backmarket = options.syncToBackMarket
      if (options.publishToBackMarket === false) {
        params.publish_to_backmarket = options.publishToBackMarket
      }
    }
    const axiosConfig = Object.keys(params).length ? { params } : undefined

    const response = await this.api.post<{
      id: number
      name: string
      sku: string
      description: string
      price: number
      category: string
      brand?: string
      stock_quantity: number
      is_active: boolean
      created_at: string
    }>('/admin/products', data, axiosConfig)
    return {
      product: response.data,
      backMarketSync: this.parseBackMarketSyncHeaders(response.headers),
    }
  }

  async updateProduct(productId: number, data: {
    name?: string
    description?: string
    short_description?: string
    meta_title?: string
    meta_description?: string
    price?: number
    original_price?: number
    category?: string
    category_id?: number
    brand?: string
    sku?: string
    stock_quantity?: number
    is_active?: boolean
    is_featured?: boolean
    is_on_sale?: boolean
    sale_percentage?: number
    sync_enabled?: boolean
    condition?: string
    weight?: number
    dimensions?: string
    ean?: string
    mpn?: string
    width?: number
    height?: number
    depth?: number
    tags?: string[]
    specifications?: Record<string, string>
    main_image?: string
    images?: string[]
    weekend_discount_percentage?: number
    weekend_discount_start?: string
    weekend_discount_end?: string
  }) {
    const response = await this.api.put<{
      id: number
      name: string
      sku: string
      description: string
      price: number
      category: string | {
        id: number
        name: string
        slug: string
        description?: string
      }
      stock_quantity: number
      is_active: boolean
      updated_at: string
    }>(`/admin/products/${productId}`, data)
    return response.data
  }

  async getSeoPages() {
    const response = await this.api.get<Array<{
      id: number
      page_key: string
      title: string
      description: string
      canonical_url?: string | null
      robots_index: boolean
      robots_follow: boolean
      created_at: string
      updated_at: string
    }>>('/admin/seo/pages')
    return response.data
  }

  async updateSeoPage(pageKey: string, data: {
    page_key: string
    title: string
    description: string
    canonical_url?: string | null
    robots_index?: boolean
    robots_follow?: boolean
  }) {
    const response = await this.api.put<{
      id: number
      page_key: string
      title: string
      description: string
      canonical_url?: string | null
      robots_index: boolean
      robots_follow: boolean
      created_at: string
      updated_at: string
    }>(`/admin/seo/pages/${pageKey}`, data)
    return response.data
  }

  async deleteProduct(productId: number) {
    const response = await this.api.delete<{ message: string }>(`/admin/products/${productId}`)
    return response.data
  }

  // CSV Import endpoints
  async importProductsCSV(file: File) {
    const formData = new FormData()
    formData.append('file', file)
    
    const response = await this.api.post<{
      success: number
      errors: number
      warnings: Array<{ row: number; message: string }>
      details: Array<{ row: number; status: string; message: string }>
    }>('/admin/import/products/csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  }

  async importCategoriesCSV(file: File) {
    const formData = new FormData()
    formData.append('file', file)
    
    const response = await this.api.post<{
      success: number
      errors: number
      warnings: Array<{ row: number; message: string }>
      details: Array<{ row: number; status: string; message: string }>
    }>('/admin/import/categories/csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  }

  // Back Market Integration endpoints
  async testBackMarketConnection() {
    const response = await this.api.get<{
      status: string
      message: string
      data?: {
        total_listings: number
        sample_available: boolean
      }
    }>('/admin/backmarket/test')
    return response.data
  }

  async syncBackMarketProducts(maxPages: number = 5) {
    const response = await this.api.post<{
      status: string
      message: string
      synced_count: number
      total_listings: number
      errors: string[]
    }>('/admin/backmarket/sync', null, {
      params: { max_pages: maxPages }
    })
    return response.data
  }

  async getBackMarketListings(cursor?: string, limit: number = 10) {
    const response = await this.api.get<{
      status: string
      data: {
        count: number
        next: string | null
        previous: string | null
        results: Array<{
          id: string
          uuid: string
          title: string
          subTitles: string[]
          displayTitle: string
          brand: string
          specifications: Array<{
            field: string
            values: Array<{
              label: string
              image?: string
            }>
          }>
          images: string[]
          referencePrice: {
            amount: string
            currency: string
          }
          category: string
          listing: {
            id: string
            price: {
              amount: string
              currency: string
            }
            grade: {
              name: string
              value: number
            }
            warranty: string
            stockWarning?: number
            sellerId: string
            pricesDifference: string
          }
          reviewRating?: {
            average: number
            count: number
          }
        }>
      }
      total_count: number
      results_count: number
      next_cursor: string | null
      previous_cursor: string | null
    }>('/admin/backmarket/listings', {
      params: { cursor, limit }
    })
    return response.data
  }

  async getBackMarketCategories() {
    const response = await this.api.get<{
      status: string
      data: Array<{
        category_id: number
        category_name: string
        marketplace_category_id: number
        is_leaf: boolean
        attributes: Array<{
          field: string
          field_name: string
          type: number
          allowed_values: string
          required: boolean
        }>
      }>
      total_categories: number
    }>('/admin/backmarket/categories')
    return response.data
  }

  async getBackMarketProduct(productId: string) {
    const response = await this.api.get<{
      status: string
      data: {
        id: string
        uuid: string
        title: string
        subTitles: string[]
        displayTitle: string
        brand: string
        specifications: Array<{
          field: string
          values: Array<{
            label: string
            image?: string
          }>
        }>
        images: string[]
        referencePrice: {
          amount: string
          currency: string
        }
        category: string
        listing: {
          id: string
          price: {
            amount: string
            currency: string
          }
          grade: {
            name: string
            value: number
          }
          warranty: string
          stockWarning?: number
          sellerId: string
          pricesDifference: string
        }
        reviewRating?: {
          average: number
          count: number
        }
      }
    }>('/admin/backmarket/products/' + productId)
    return response.data
  }

  // Reports endpoints
  async getSalesReport(dateRange: string = '30d') {
    const response = await this.api.get<{
      status: string
      data: {
        sales_data: Array<{
          period: string
          revenue: number
          orders: number
          avg_order_value: number
          growth_rate: number
          source: string
        }>
        top_products: Array<{
          id: number
          name: string
          revenue: number
          quantity_sold: number
        }>
        summary: {
          total_revenue: number
          total_orders: number
          revenue_30d: number
          new_orders_30d: number
        }
      }
    }>('/admin/reports/sales', { params: { date_range: dateRange } })
    return response.data
  }

  async getF8InternalSalesReport(dateRange: string = '30d') {
    const response = await this.api.get<{
      status: string
      data: {
        sales_data: Array<{
          period: string
          revenue: number
          orders: number
          avg_order_value: number
          growth_rate: number
          source: string
        }>
        top_products: Array<{
          id: number
          name: string
          revenue: number
          quantity_sold: number
        }>
        summary: {
          total_revenue: number
          total_orders: number
          revenue_30d: number
          new_orders_30d: number
        }
      }
    }>('/admin/reports/sales/f8-internal', { params: { date_range: dateRange } })
    return response.data
  }

  async getBackMarketSalesReport(dateRange: string = '30d') {
    const response = await this.api.get<{
      status: string
      data: {
        sales_data: Array<{
          period: string
          revenue: number
          listings_sold: number
          commission_earned: number
          avg_listing_value: number
          growth_rate: number
          source: string
        }>
        top_products: Array<{
          id: number
          name: string
          price: number
          stock_quantity: number
          external_id: string
          category: string
        }>
        summary: {
          total_listings: number
          active_listings: number
          total_listing_value: number
          total_commission_30d: number
          avg_commission_rate: number
        }
      }
    }>('/admin/reports/sales/backmarket', { params: { date_range: dateRange } })
    return response.data
  }

  async getInventoryReport() {
    const response = await this.api.get<{
      status: string
      data: {
        inventory: Array<{
          id: number
          name: string
          sku: string
          category: string
          current_stock: number
          reserved_stock: number
          available_stock: number
          reorder_level: number
          status: string
          unit_price: number
          total_value: number
          last_restocked: string | null
          source: string
        }>
        stats: {
          total_products: number
          low_stock_products: number
          out_of_stock_products: number
          in_stock_products: number
          total_stock_value: number
        }
      }
    }>('/admin/reports/inventory')
    return response.data
  }

  async getInternalOrders(params?: { skip?: number; limit?: number; status?: string }) {
    const response = await this.api.get<{
      status: string
      data: Array<{
        id: number
        order_number: string
        customer_name: string
        customer_email: string
        total_amount: number
        status: string
        source: string
        created_at: string
        items_count: number
        cancellation_reason?: string
      }>
    }>('/admin/orders/internal', { params })
    return response.data
  }

  async getBackMarketOrders(params?: { skip?: number; limit?: number; status?: string }) {
    const response = await this.api.get<{
      status: string
      data: Array<{
        id: number
        backmarket_order_id: string
        order_number: string
        customer_name: string
        total_amount: number
        status: string
        sync_status: string
        created_at: string
        items_count: number
        commission: number
        source: string
      }>
      note?: string
    }>('/admin/orders/backmarket', { params })
    return response.data
  }

  // Settings endpoints
  // Settings endpoints
  // Custom Shipping Methods (admin-configured, shown at checkout with Shippo)
  async getCustomShippingMethods() {
    const response = await this.api.get<Array<{
      id: number
      name: string
      description: string | null
      type: string
      amount: number
      min_order_amount: number | null
      countries: string | null
      is_active: boolean
      sort_order: number
      created_at: string | null
      updated_at: string | null
    }>>('/admin/shipping-methods')
    return response.data
  }

  async createCustomShippingMethod(data: {
    name: string
    description?: string
    type: string
    amount: number
    min_order_amount?: number
    countries?: string
    is_active?: boolean
    sort_order?: number
  }) {
    const response = await this.api.post<{ id: number; message: string }>('/admin/shipping-methods', data)
    return response.data
  }

  async updateCustomShippingMethod(id: number, data: {
    name?: string
    description?: string
    type?: string
    amount?: number
    min_order_amount?: number
    countries?: string
    is_active?: boolean
    sort_order?: number
  }) {
    const response = await this.api.put<{ message: string }>(`/admin/shipping-methods/${id}`, data)
    return response.data
  }

  async deleteCustomShippingMethod(id: number) {
    const response = await this.api.delete<{ message: string }>(`/admin/shipping-methods/${id}`)
    return response.data
  }

  async getGeneralSettings() {
    const response = await this.api.get<{
      status: string
      data: {
        site_name: string
        site_description: string
        site_url: string
        admin_email: string
        support_email: string
        company_name: string
        company_address: string
        company_phone: string
        currency: string
        timezone: string
        date_format: string
        items_per_page: number
        maintenance_mode: boolean
        registration_enabled: boolean
      }
    }>('/admin/settings/general')
    return response.data
  }

  async updateGeneralSettings(settings: {
    site_name?: string
    site_description?: string
    site_url?: string
    admin_email?: string
    support_email?: string
    company_name?: string
    company_address?: string
    company_phone?: string
    currency?: string
    timezone?: string
    date_format?: string
    items_per_page?: number
    maintenance_mode?: boolean
    registration_enabled?: boolean
  }) {
    const response = await this.api.put<{
      status: string
      message: string
      data: any
    }>('/admin/settings/general', settings)
    return response.data
  }

  async getIntegrationSettings() {
    const response = await this.api.get<{
      backmarket?: {
        id: number
        api_key: string
        api_secret: string
        environment: 'sandbox' | 'production'
        webhook_url: string
        sync_enabled: boolean
        condition: string
        auto_sync_products: boolean
        auto_sync_orders: boolean
        auto_sync_inventory: boolean
        sync_interval: number
        created_at: string
        updated_at: string
      }
      email?: {
        id: number
        smtp_host: string
        smtp_port: number
        smtp_username: string
        smtp_password: string
        smtp_encryption: 'none' | 'tls' | 'ssl'
        from_email: string
        from_name: string
        created_at: string
        updated_at: string
      }
    }>('/settings/integrations')
    return response.data
  }

  async updateBackMarketSettings(settings: {
    api_key?: string
    api_secret?: string
    environment?: 'sandbox' | 'production'
    webhook_url?: string
    sync_enabled?: boolean
    condition?: string
    auto_sync_products?: boolean
    auto_sync_orders?: boolean
    auto_sync_inventory?: boolean
    sync_interval?: number
  }) {
    const response = await this.api.put<{
      id: number
      api_key: string
      api_secret: string
      environment: 'sandbox' | 'production'
      webhook_url: string
      sync_enabled: boolean
      condition: string
      auto_sync_products: boolean
      auto_sync_orders: boolean
      auto_sync_inventory: boolean
      sync_interval: number
      created_at: string
      updated_at: string
    }>('/settings/backmarket', settings)
    return response.data
  }

  async updateEmailSettings(settings: {
    smtp_host?: string
    smtp_port?: number
    smtp_username?: string
    smtp_password?: string
    smtp_encryption?: 'none' | 'tls' | 'ssl'
    from_email?: string
    from_name?: string
  }) {
    const response = await this.api.put<{
      id: number
      smtp_host: string
      smtp_port: number
      smtp_username: string
      smtp_password: string
      smtp_encryption: 'none' | 'tls' | 'ssl'
      from_email: string
      from_name: string
      created_at: string
      updated_at: string
    }>('/settings/email', settings)
    return response.data
  }

  // Slide Management endpoints
  async getSliderImages(params?: {
    skip?: number
    limit?: number
    active_only?: boolean
  }) {
    const response = await this.api.get<{
      status: string
      data: Array<{
        id: number
        title: string
        description: string | null
        image_url: string
        alt_text: string | null
        link_url: string | null
        link_text: string | null
        open_new_tab: boolean
        is_active: boolean
        display_order: number
        mobile_image_url: string | null
        text_position: string
        text_color: string
        animation_type: string
        display_duration: number
        created_at: string
        updated_at: string
      }>
      count: number
    }>('/admin/slider-images', { params })
    return response.data
  }

  async getSliderImage(sliderId: number) {
    const response = await this.api.get<{
      id: number
      title: string
      description: string | null
      image_url: string
      alt_text: string | null
      link_url: string | null
      link_text: string | null
      open_new_tab: boolean
      is_active: boolean
      display_order: number
      mobile_image_url: string | null
      text_position: string
      text_color: string
      animation_type: string
      display_duration: number
      created_at: string
      updated_at: string
    }>(`/admin/slider-images/${sliderId}`)
    return response.data
  }

  async createSliderImage(data: {
    title: string
    description?: string
    image_url: string
    alt_text?: string
    link_url?: string
    link_text?: string
    open_new_tab?: boolean
    is_active?: boolean
    display_order?: number
    mobile_image_url?: string
    text_position?: string
    text_color?: string
    animation_type?: string
    display_duration?: number
    // Banner-specific fields
    subtitle?: string
    price_text?: string
    button_text?: string
    background_class?: string
  }) {
    const response = await this.api.post<{
      id: number
      title: string
      description: string | null
      image_url: string
      alt_text: string | null
      link_url: string | null
      link_text: string | null
      open_new_tab: boolean
      is_active: boolean
      display_order: number
      mobile_image_url: string | null
      text_position: string
      text_color: string
      animation_type: string
      display_duration: number
      created_at: string
      updated_at: string
    }>('/admin/slider-images', data)
    return response.data
  }

  async updateSliderImage(sliderId: number, data: {
    title?: string
    description?: string
    image_url?: string
    alt_text?: string
    link_url?: string
    link_text?: string
    open_new_tab?: boolean
    is_active?: boolean
    display_order?: number
    mobile_image_url?: string
    text_position?: string
    text_color?: string
    animation_type?: string
    display_duration?: number
    // Banner-specific fields
    subtitle?: string
    price_text?: string
    button_text?: string
    background_class?: string
  }) {
    const response = await this.api.put<{
      id: number
      title: string
      description: string | null
      image_url: string
      alt_text: string | null
      link_url: string | null
      link_text: string | null
      open_new_tab: boolean
      is_active: boolean
      display_order: number
      mobile_image_url: string | null
      text_position: string
      text_color: string
      animation_type: string
      display_duration: number
      created_at: string
      updated_at: string
    }>(`/admin/slider-images/${sliderId}`, data)
    return response.data
  }

  async deleteSliderImage(sliderId: number) {
    const response = await this.api.delete<{ message: string }>(`/admin/slider-images/${sliderId}`)
    return response.data
  }

  async reorderSliderImages(sliderIds: number[]) {
    const response = await this.api.put<{ message: string }>('/admin/slider-images/reorder', {
      slider_ids: sliderIds
    })
    return response.data
  }


  // Manual sync endpoints - using actual backend endpoints
  async syncProducts(maxPages: number = 5) {
    const response = await this.api.post<{
      status: string
      message: string
      synced_count: number
      total_listings: number
      errors: string[]
    }>('/admin/backmarket/sync', null, {
      params: { max_pages: maxPages }
    })
    return response.data
  }

  async syncProductsAsync(maxPages: number = 5) {
    const response = await this.api.post<{
      status: string
      message: string
      task_id: string
      max_pages: number
    }>('/admin/backmarket/sync/async', null, {
      params: { max_pages: maxPages }
    })
    return response.data
  }

  async syncOrders(daysBack: number = 7) {
    const response = await this.api.post<{
      status: string
      message: string
      sync_type: string
    }>('/admin/sync/orders', null, {
      params: { days_back: daysBack }
    })
    return response.data
  }

  async syncInventory(productIds?: number[]) {
    const response = await this.api.post<{
      status: string
      message: string
      sync_type: string
      product_ids?: number[]
    }>('/admin/sync/inventory', {
      product_ids: productIds || []
    })
    return response.data
  }

  // Banner Management (Admin Only)
  async getBanners() {
    const response = await this.api.get('/admin/banners')
    return response.data
  }

  async getBanner(bannerId: number) {
    const response = await this.api.get(`/admin/banners/${bannerId}`)
    return response.data
  }

  async getBannerByPage(pageName: string) {
    const response = await this.api.get(`/admin/banners/page/${pageName}`)
    return response.data
  }

  async createBanner(data: {
    page_name: string
    title: string
    image_url: string
    mobile_image_url?: string
    alt_text?: string
    subtitle?: string
    description?: string
    product_image_url?: string
    price?: string
    price_label?: string
    link_url?: string
    button_text?: string
    title_line_1?: string
    title_line_2?: string
    title_line_3?: string
    sale_percentage?: string
    is_active?: boolean
    text_color?: string
    overlay_opacity?: number
  }) {
    const response = await this.api.post('/admin/banners/', data)
    return response.data
  }

  async updateBanner(bannerId: number, data: {
    title?: string
    image_url?: string
    mobile_image_url?: string
    alt_text?: string
    subtitle?: string
    description?: string
    product_image_url?: string
    price?: string
    price_label?: string
    link_url?: string
    button_text?: string
    title_line_1?: string
    title_line_2?: string
    title_line_3?: string
    sale_percentage?: string
    is_active?: boolean
    text_color?: string
    overlay_opacity?: number
  }) {
    const response = await this.api.put(`/admin/banners/${bannerId}`, data)
    return response.data
  }

  async deleteBanner(bannerId: number) {
    const response = await this.api.delete(`/admin/banners/${bannerId}`)
    return response.data
  }

  async toggleBannerStatus(bannerId: number) {
    const response = await this.api.patch(`/admin/banners/${bannerId}/toggle`)
    return response.data
  }

  async getSyncTaskStatus(taskId: string) {
    const response = await this.api.get<{
      status: string
      message: string
      task_id: string
      state: string
      progress?: any
      result?: any
      error?: string
    }>(`/admin/backmarket/sync/status/${taskId}`)
    return response.data
  }

  // Sync logs endpoints
  async getSyncLogs(params?: {
    skip?: number
    limit?: number
    sync_type?: string
    sync_status?: string
  }) {
    const response = await this.api.get<{
      status: string
      data: {
        logs: Array<{
          id: number
          sync_type: string
          status: string
          started_at: string
          completed_at: string | null
          duration_seconds: number | null
          records_processed: number
          records_success: number
          records_failed: number
          error_message: string | null
          triggered_by: string
          retry_count: number
        }>
        total_count: number
        skip: number
        limit: number
      }
    }>('/admin/sync/logs', { params })
    return response.data
  }

  // General Sync API endpoints
  async getGeneralSyncLogs(params?: {
    job_type?: string
    source?: string
    status?: string
    start_date?: string
    end_date?: string
    limit?: number
    offset?: number
  }) {
    const response = await this.api.get<{
      status: string
      data: {
        logs: Array<{
          id: number
          sync_type: string
          status: string
          started_at: string
          completed_at: string | null
          duration_seconds: number | null
          records_processed: number
          records_success: number
          records_failed: number
          error_message: string | null
          triggered_by: string
          retry_count: number
        }>
        total_count: number
        skip: number
        limit: number
      }
    }>('/admin/sync/logs', { params })
    return response.data
  }

  async getSyncLogDetails(logId: number) {
    const response = await this.api.get<{
      id: number
      job_type: string
      source: string
      status: string
      error_message: string | null
      error_details: any
      run_at: string
      finished_at: string | null
      duration_seconds: number | null
      retries: number
      max_retries: number
      records_processed: number
      records_successful: number
      records_failed: number
      job_parameters: any
      result_data: any
      external_reference_id: string | null
    }>(`/admin/sync/logs/${logId}`)
    return response.data
  }

  async getSyncSchedules(params?: {
    source?: string
    enabled?: boolean
  }) {
    const response = await this.api.get<Array<{
      id: number
      sync_type: string
      source: string
      enabled: boolean
      interval_minutes: number
      next_run: string | null
      last_run: string | null
      sync_parameters: any
      max_concurrent_jobs: number
      retry_on_failure: boolean
      max_retries: number
      retry_delay_minutes: number
      consecutive_failures: number
      max_consecutive_failures: number
      health_status: string
      created_at: string
      updated_at: string
    }>>('/admin/sync/schedules', { params })
    return response.data
  }

  async createSyncSchedule(data: {
    sync_type: string
    source: string
    enabled?: boolean
    interval_minutes?: number
    sync_parameters?: any
    max_concurrent_jobs?: number
    retry_on_failure?: boolean
    max_retries?: number
    retry_delay_minutes?: number
    max_consecutive_failures?: number
  }) {
    const response = await this.api.post<{
      id: number
      sync_type: string
      source: string
      enabled: boolean
      interval_minutes: number
      next_run: string | null
      last_run: string | null
      sync_parameters: any
      max_concurrent_jobs: number
      retry_on_failure: boolean
      max_retries: number
      retry_delay_minutes: number
      consecutive_failures: number
      max_consecutive_failures: number
      health_status: string
      created_at: string
      updated_at: string
    }>('/admin/sync/schedules', data)
    return response.data
  }

  async updateSyncSchedule(scheduleId: number, data: {
    enabled?: boolean
    interval_minutes?: number
    sync_parameters?: any
    max_concurrent_jobs?: number
    retry_on_failure?: boolean
    max_retries?: number
    retry_delay_minutes?: number
    max_consecutive_failures?: number
    next_run?: string
    health_status?: string
  }) {
    const response = await this.api.put<{
      id: number
      sync_type: string
      source: string
      enabled: boolean
      interval_minutes: number
      next_run: string | null
      last_run: string | null
      sync_parameters: any
      max_concurrent_jobs: number
      retry_on_failure: boolean
      max_retries: number
      retry_delay_minutes: number
      consecutive_failures: number
      max_consecutive_failures: number
      health_status: string
      created_at: string
      updated_at: string
    }>(`/admin/sync/schedules/${scheduleId}`, data)
    return response.data
  }

  async deleteSyncSchedule(scheduleId: number) {
    const response = await this.api.delete<{ message: string }>(`/admin/sync/schedules/${scheduleId}`)
    return response.data
  }

  async getSyncStatistics(days?: number) {
    const response = await this.api.get<{
      total_jobs: number
      successful_jobs: number
      failed_jobs: number
      pending_jobs: number
      in_progress_jobs: number
      average_duration: number | null
      success_rate: number
      jobs_by_type: Record<string, number>
      jobs_by_source: Record<string, number>
      recent_failures: Array<{
        id: number
        job_type: string
        source: string
        error_message: string | null
        run_at: string
        retries: number
      }>
    }>('/admin/sync/statistics', { params: { days } })
    return response.data
  }

  // Image upload
  async uploadImage(formData: FormData) {
    const response = await this.api.post<{
      url: string
      public_id: string
      secure_url: string
    }>('/images/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  }

  // Product image upload (dedicated method for products)
  async uploadProductImage(file: File, folder: string = 'products') {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('folder', folder)

    // Get admin token for authentication (same pattern as slider upload)
    const token = localStorage.getItem('admin_token')
    if (!token) {
      throw new Error('No authentication token found')
    }

    const response = await this.api.post<{
      success: boolean
      public_id: string
      url: string
      width: number
      height: number
      format: string
      resource_type: string
    }>('/images/upload/single', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      params: {
        token: token
      }
    })
    return response.data
  }

  // Upload slider image to specific folder
  async uploadSliderImage(file: File, folder: string = 'slider-image') {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('folder', folder)
    
    // Get admin token for authentication
    const token = localStorage.getItem('admin_token')
    if (!token) {
      throw new Error('No authentication token found')
    }
    
    const response = await this.api.post<{
      success: boolean
      public_id: string
      url: string
      width: number
      height: number
      format: string
      resource_type: string
    }>('/images/upload/single', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      params: {
        token: token
      }
    })
    return response.data
  }

  // Upload banner image to specific folder
  async uploadBannerImage(file: File, folder: string = 'banners') {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('folder', folder)
    
    // Get admin token for authentication
    const token = localStorage.getItem('admin_token')
    if (!token) {
      throw new Error('No authentication token found')
    }
    
    const response = await this.api.post<{
      success: boolean
      public_id: string
      url: string
      width: number
      height: number
      format: string
      resource_type: string
    }>('/images/upload/single', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      params: {
        token: token
      }
    })
    return response.data
  }

  // Public endpoints - no authentication required
  async getPublicSliderImages(params?: {
    limit?: number
  }) {
    const response = await this.api.get<{
      status: string
      data: Array<{
        id: number
        title: string
        description: string | null
        image_url: string
        alt_text: string | null
        link_url: string | null
        link_text: string | null
        open_new_tab: boolean
        is_active: boolean
        display_order: number
        mobile_image_url: string | null
        text_position: string
        text_color: string
        animation_type: string
        display_duration: number
        created_at: string
        updated_at: string
      }>
      count: number
    }>('/public/slider-images', { params })
    return response.data
  }

  async getPublicSliderImage(sliderId: number) {
    const response = await this.api.get<{
      id: number
      title: string
      description: string | null
      image_url: string
      alt_text: string | null
      link_url: string | null
      link_text: string | null
      open_new_tab: boolean
      is_active: boolean
      display_order: number
      mobile_image_url: string | null
      text_position: string
      text_color: string
      animation_type: string
      display_duration: number
      created_at: string
      updated_at: string
    }>(`/public/slider-images/${sliderId}`)
    return response.data
  }

  // ===========================
  // REVIEWS API METHODS
  // ===========================

  // Admin Review Management
  async getAdminReviews(params: {
    skip?: number
    limit?: number
    product_id?: number
    user_id?: number
    rating?: number
    is_approved?: boolean
    search?: string
  } = {}) {
    const response = await this.api.get<{
      status: string
      data: Array<{
        id: number
        product_id: number
        user: {
          id: number
          full_name: string
        }
        rating: number
        title: string
        comment: string | null
        helpful_count: number
        created_at: string
        updated_at: string
        is_approved: boolean
        is_verified_purchase: boolean
        product_name: string | null
      }>
      total: number
    }>('/admin/reviews', { params })
    return response.data
  }

  async approveReview(reviewId: number, approved: boolean) {
    const response = await this.api.put<{
      status: string
      message: string
      data: {
        id: number
        product_id: number
        user: {
          id: number
          full_name: string
        }
        rating: number
        title: string
        comment: string | null
        helpful_count: number
        created_at: string
        updated_at: string
        is_approved: boolean
        is_verified_purchase: boolean
      }
    }>(`/admin/reviews/${reviewId}/approve?approved=${approved}`)
    return response.data
  }

  async deleteReviewAdmin(reviewId: number) {
    const response = await this.api.delete<{
      status: string
      message: string
    }>(`/admin/reviews/${reviewId}`)
    return response.data
  }

  async createReviewAdmin(data: {
    product_id: number
    user_id: number
    rating: number
    title: string
    comment?: string
    is_approved?: boolean
  }) {
    const response = await this.api.post<{
      status: string
      data: {
        id: number
        product_id: number
        user: { id: number; full_name: string }
        rating: number
        title: string
        comment: string | null
        helpful_count: number
        created_at: string
        updated_at: string
      }
    }>('/admin/reviews/create', data)
    return response.data
  }

  async getReviewStats() {
    const response = await this.api.get<{
      status: string
      data: {
        total_reviews: number
        approved_reviews: number
        pending_reviews: number
        average_rating: number
        rating_distribution: Record<string, number>
        recent_reviews_week: number
        top_products: Array<{
          product_id: number
          product_name: string
          review_count: number
          average_rating: number
        }>
      }
    }>('/admin/reviews/stats')
    return response.data
  }

  // Public Review Endpoints
  async getProductReviews(productId: number, params: {
    skip?: number
    limit?: number
    rating_filter?: number
  } = {}) {
    const response = await this.api.get<Array<{
      id: number
      product_id: number
      user: {
        id: number
        first_name: string
        last_name: string
      }
      rating: number
      title: string
      comment: string | null
      helpful_count: number
      created_at: string
      updated_at: string
    }>>(`/reviews/products/${productId}/reviews`, { params })
    return response.data
  }

  async getProductReviewStats(productId: number) {
    const response = await this.api.get<{
      total_reviews: number
      average_rating: number
      rating_distribution: Record<string, number>
    }>(`/reviews/products/${productId}/stats`)
    return response.data
  }

  async getProductReviewSummary(productId: number) {
    const response = await this.api.get<{
      stats: {
        total_reviews: number
        average_rating: number
        rating_distribution: Record<string, number>
      }
      recent_reviews: Array<{
        id: number
        product_id: number
        user: {
          id: number
          first_name: string
          last_name: string
        }
        rating: number
        title: string
        comment: string | null
        helpful_count: number
        created_at: string
        updated_at: string
      }>
      product_id: number
    }>(`/reviews/products/${productId}/summary`)
    return response.data
  }

  async getReviewById(reviewId: number) {
    const response = await this.api.get<{
      id: number
      product_id: number
      user: {
        id: number
        first_name: string
        last_name: string
      }
      rating: number
      title: string
      comment: string | null
      helpful_count: number
      created_at: string
      updated_at: string
    }>(`/reviews/${reviewId}`)
    return response.data
  }

  // Policy Management
  async getAllPolicies() {
    const response = await this.api.get('/policies/')
    return response.data
  }

  async getPolicy(policyType: string) {
    const response = await this.api.get(`/policies/${policyType}`)
    return response.data
  }

  async createPolicy(data: {
    policy_type: string
    title: string
    content: string
    is_active: boolean
  }) {
    const response = await this.api.post('/policies/', data)
    return response.data
  }

  async updatePolicy(policyType: string, data: {
    title?: string
    content?: string
    is_active?: boolean
  }) {
    const response = await this.api.put(`/policies/${policyType}`, data)
    return response.data
  }

  async deletePolicy(policyType: string) {
    const response = await this.api.delete(`/policies/${policyType}`)
    return response.data
  }

  // Two-Factor Authentication (2FA)
  async enable2FA() {
    const response = await this.api.post<{
      success: boolean
      secret: string
      qr_code: string
      message: string
    }>('/2fa/admin/enable')
    return response.data
  }

  async verify2FA(token: string, secret: string) {
    const response = await this.api.post<{
      success: boolean
      message: string
    }>('/2fa/admin/verify', { token, secret })
    return response.data
  }

  async get2FAStatus() {
    const response = await this.api.get<{
      success: boolean
      enabled: boolean
      has_secret: boolean
    }>('/2fa/admin/status')
    return response.data
  }

  async disable2FA() {
    const response = await this.api.post<{
      success: boolean
      message: string
    }>('/2fa/admin/disable')
    return response.data
  }

  // Audit Logs
  async getAuditLogs(params?: {
    skip?: number
    limit?: number
    action_type?: string
    user_id?: number
    admin_id?: number
    resource_type?: string
    start_date?: string
    end_date?: string
    success?: string
  }) {
    const response = await this.api.get<Array<{
      id: number
      action_type: string
      action_description: string
      user_id: number | null
      admin_id: number | null
      user_email: string | null
      ip_address: string | null
      user_agent: string | null
      request_method: string | null
      request_path: string | null
      resource_type: string | null
      resource_id: number | null
      old_values: any
      new_values: any
      metadata: any
      success: string
      error_message: string | null
      created_at: string
    }>>('/admin/audit-logs', { params })
    return response.data
  }

  async getAuditLog(logId: number) {
    const response = await this.api.get<{
      id: number
      action_type: string
      action_description: string
      user_id: number | null
      admin_id: number | null
      user_email: string | null
      ip_address: string | null
      user_agent: string | null
      request_method: string | null
      request_path: string | null
      resource_type: string | null
      resource_id: number | null
      old_values: any
      new_values: any
      metadata: any
      success: string
      error_message: string | null
      created_at: string
    }>(`/admin/audit-logs/${logId}`)
    return response.data
  }

  async exportAuditLogs(params?: any) {
    const response = await this.api.get('/admin/audit-logs/export', {
      params,
      responseType: 'blob'
    })
    return response.data
  }

  // Admin Coupon Management
  async getAdminCoupons(params?: { skip?: number; limit?: number; include_inactive?: boolean }) {
    const response = await this.api.get<{
      data: Array<{
        id: number
        code: string
        description: string | null
        discount_type: string
        discount_value: number
        min_order_amount: number | null
        max_discount: number | null
        applies_to: string
        auto_apply: boolean
        exclusive: boolean
        product_ids: number[]
        products: Array<{ id: number; name: string; sku: string | null; price: number }>
        usage_limit: number | null
        used_count: number
        usage_limit_per_user: number | null
        valid_from: string | null
        valid_to: string | null
        is_active: boolean
        created_at: string
        updated_at: string
      }>
      total: number
    }>('/admin/coupons', { params })
    return response.data
  }

  async createCoupon(data: {
    code: string
    description?: string
    discount_type: 'percentage' | 'fixed'
    discount_value: number
    min_order_amount?: number
    max_discount?: number
    applies_to?: string
    auto_apply?: boolean
    exclusive?: boolean
    product_ids?: number[]
    usage_limit?: number
    usage_limit_per_user?: number
    valid_from?: string
    valid_to?: string
    is_active?: boolean
  }) {
    const response = await this.api.post<{
      status: string
      data: {
        id: number
        code: string
        description: string | null
        discount_type: string
        discount_value: number
        min_order_amount: number | null
        max_discount: number | null
        applies_to: string
        auto_apply: boolean
        exclusive: boolean
        product_ids: number[]
        products: Array<{ id: number; name: string; sku: string | null; price: number }>
        usage_limit: number | null
        used_count: number
        usage_limit_per_user: number | null
        valid_from: string | null
        valid_to: string | null
        is_active: boolean
        created_at: string
        updated_at: string
      }
    }>('/admin/coupons', data)
    return response.data
  }

  async updateCoupon(couponId: number, data: Partial<{
    description: string
    discount_type: string
    discount_value: number
    min_order_amount: number
    max_discount: number
    applies_to: string
    auto_apply: boolean
    exclusive: boolean
    product_ids: number[]
    usage_limit: number
    usage_limit_per_user: number
    valid_from: string
    valid_to: string
    is_active: boolean
  }>) {
    const response = await this.api.put<{
      status: string
      data: {
        id: number
        code: string
        description: string | null
        discount_type: string
        discount_value: number
        min_order_amount: number | null
        max_discount: number | null
        applies_to: string
        auto_apply: boolean
        exclusive: boolean
        product_ids: number[]
        products: Array<{ id: number; name: string; sku: string | null; price: number }>
        usage_limit: number | null
        used_count: number
        usage_limit_per_user: number | null
        valid_from: string | null
        valid_to: string | null
        is_active: boolean
        created_at: string
        updated_at: string
      }
    }>(`/admin/coupons/${couponId}`, data)
    return response.data
  }

  async deleteCoupon(couponId: number) {
    const response = await this.api.delete<{
      status: string
      message: string
    }>(`/admin/coupons/${couponId}`)
    return response.data
  }

  async searchProductsForCoupon(search: string = '', limit: number = 20) {
    const response = await this.api.get<{
      data: Array<{ id: number; name: string; sku: string | null; price: number }>
    }>('/admin/coupons/products-search', { params: { search, limit } })
    return response.data
  }

  // Password Change
  async changePassword(currentPassword: string, newPassword: string) {
    const response = await this.api.put<{
      success: boolean
      message: string
    }>('/admin/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    })
    return response.data
  }

  // Session Management
  async getAdminSessions(activeOnly: boolean = true) {
    const response = await this.api.get<Array<{
      id: number
      admin_id: number
      ip_address: string | null
      user_agent: string | null
      device_info: any
      location_info: any
      is_active: boolean
      last_activity: string
      created_at: string
      expires_at: string
    }>>('/sessions/admin/sessions', {
      params: { active_only: activeOnly }
    })
    return response.data
  }

  async revokeAdminSession(sessionId: number) {
    const response = await this.api.post<{
      message: string
    }>(`/sessions/admin/sessions/${sessionId}/revoke`)
    return response.data
  }

  async revokeAllAdminSessions() {
    const response = await this.api.post<{
      message: string
    }>('/sessions/admin/sessions/revoke-all')
    return response.data
  }

  // ---------------------------------------------------------------- eBay
  // The integration is inbound: listings, stock and metrics flow from eBay into the
  // catalogue, and storefront sales are pushed back as quantity updates. There are no
  // eBay orders here by design — like BackMarket, orders are the channel's own concern.

  async getEbayStatus() {
    const response = await this.api.get<{
      connected: boolean
      ebay_user_id?: string
      environment?: string
      marketplace_id?: string
      granted_scopes?: string[]
      access_token_expires_at?: string
      refresh_token_expires_at?: string
      refresh_days_remaining?: number
      refresh_needs_attention?: boolean
      last_used_at?: string
      last_refreshed_at?: string
      rate_limit?: Record<string, unknown>
      pending_push_products?: number
      push_enabled?: boolean
      auto_activate_products?: boolean
      id?: number
    }>('/ebay/status')
    return response.data
  }

  async getEbayAuthorizeUrl() {
    const response = await this.api.get<{
      url: string
      state: string
      scopes: string[]
      environment: string
    }>('/ebay/authorize-url')
    return response.data
  }

  async updateEbaySettings(autoActivateProducts: boolean) {
    const response = await this.api.put<{
      auto_activate_products: boolean
      push_enabled: boolean
    }>('/ebay/settings', { auto_activate_products: autoActivateProducts })
    return response.data
  }

  async getEbayListings() {
    const response = await this.api.get<{
      listings: Array<{
        item_id: string
        title: string
        price: number | null
        quantity_available: number
        quantity_total: number
        quantity_sold: number
        condition: string | null
        ebay_category: string | null
        product_id: number | null
        product_active: boolean | null
        local_stock: number | null
        pending_push: number
      }>
      total: number
      linked: number
      unlinked: number
      live_on_storefront: number
      pending_push: number
      stock_value: number
    }>('/ebay/listings')
    return response.data
  }

  async syncEbayNow() {
    const response = await this.api.post<{
      status: string
      fetched?: number
      created?: number
      updated?: number
      adopted?: number
      failed?: number
      reason?: string
    }>('/ebay/sync')
    return response.data
  }

  async disconnectEbay(authId: number) {
    const response = await this.api.delete<{ status: string; ebay_user_id: string }>(
      `/ebay/authorization/${authId}`
    )
    return response.data
  }

  // Product sources (admin-managed taxonomy: eBay, Facebook, BackMarket, etc.)
  async getSources(params?: { skip?: number; limit?: number; is_active?: boolean; search?: string }) {
    const response = await this.api.get<{
      items: Array<{
        id: number
        name: string
        slug: string
        description?: string
        is_active: boolean
        sort_order: number
        created_at: string
        updated_at: string
      }>
      total: number
    }>('/sources', { params })
    return response.data
  }

  async createSource(data: { name: string; description?: string; is_active?: boolean; sort_order?: number }) {
    const response = await this.api.post<{
      message: string
      source: {
        id: number
        name: string
        slug: string
        description?: string
        is_active: boolean
        sort_order: number
        created_at: string
        updated_at: string
      }
    }>('/sources', data)
    return response.data
  }

  async updateSource(sourceId: number, data: { name?: string; description?: string; is_active?: boolean; sort_order?: number }) {
    const response = await this.api.put<{
      id: number
      name: string
      slug: string
      description?: string
      is_active: boolean
      sort_order: number
      created_at: string
      updated_at: string
    }>(`/sources/${sourceId}`, data)
    return response.data
  }

  async deleteSource(sourceId: number) {
    await this.api.delete(`/sources/${sourceId}`)
  }

  // Internal/custom invoices
  async getInvoices(params?: {
    skip?: number
    limit?: number
    search?: string
    date_from?: string
    date_to?: string
  }) {
    const response = await this.api.get<{
      items: Array<AdminInvoice>
      total: number
    }>('/admin/invoices', { params })
    return response.data
  }

  async getInvoice(invoiceId: number) {
    const response = await this.api.get<AdminInvoice>(`/admin/invoices/${invoiceId}`)
    return response.data
  }

  async createInvoice(data: AdminInvoiceInput) {
    const response = await this.api.post<{
      message: string
      invoice: AdminInvoice
    }>('/admin/invoices', data)
    return response.data
  }

  async updateInvoice(invoiceId: number, data: Partial<AdminInvoiceInput>) {
    const response = await this.api.put<AdminInvoice>(`/admin/invoices/${invoiceId}`, data)
    return response.data
  }

  async deleteInvoice(invoiceId: number) {
    await this.api.delete(`/admin/invoices/${invoiceId}`)
  }

  async createInvoiceFromBackMarketOrder(backmarketOrderId: number) {
    const response = await this.api.post<{
      message: string
      invoice: AdminInvoice
    }>(`/admin/invoices/from-backmarket-order/${backmarketOrderId}`)
    return response.data
  }
}

export interface AdminInvoiceItem {
  id?: number
  product_id?: number | null
  description: string
  quantity: number
  unit_price: number | string
  amount?: number | string
}

export interface AdminInvoice {
  id: number
  invoice_number: string
  invoice_date: string
  bill_to_name: string
  bill_to_address_line_1?: string | null
  bill_to_address_line_2?: string | null
  bill_to_city?: string | null
  bill_to_state?: string | null
  bill_to_postal_code?: string | null
  bill_to_country?: string | null
  ship_to_name?: string | null
  ship_to_address_line_1?: string | null
  ship_to_address_line_2?: string | null
  ship_to_city?: string | null
  ship_to_state?: string | null
  ship_to_postal_code?: string | null
  ship_to_country?: string | null
  subtotal: number | string
  discount_type?: string
  discount_value?: number | string
  discount_amount?: number | string
  total: number | string
  currency: string
  note?: string | null
  order_id?: number | null
  items: AdminInvoiceItem[]
  created_at: string
  updated_at: string
}

export interface AdminInvoiceInput {
  invoice_number?: string
  invoice_date?: string
  bill_to_name: string
  bill_to_address_line_1?: string
  bill_to_address_line_2?: string
  bill_to_city?: string
  bill_to_state?: string
  bill_to_postal_code?: string
  bill_to_country?: string
  ship_to_name?: string
  ship_to_address_line_1?: string
  ship_to_address_line_2?: string
  ship_to_city?: string
  ship_to_state?: string
  ship_to_postal_code?: string
  ship_to_country?: string
  currency?: string
  note?: string
  order_id?: number
  discount_type?: 'fixed' | 'percentage'
  discount_value?: number
  items: Array<{
    product_id?: number | null
    description: string
    quantity: number
    unit_price: number
  }>
}

export const apiService = new ApiService()
